import { Component, OnInit, inject, signal } from '@angular/core';
import { Meta } from '@angular/platform-browser';

import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GrandCompteDnsService } from '@core/services/grand-compte-dns.service';
import { AppFacade } from '@core/services/facades/app.facade';
import { AuthService } from '@core/services/auth.service';
import { SessionExpiredService } from '@core/services/session-expired.service';
import { environment } from '@environments/environment';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { NotificationService } from './core/services/notification.service';
import { NotificationComponent } from '@shared/components/notification/notification.component';
import { DebugPanelComponent } from '@shared/components/debug-panel/debug-panel.component';
import { FALLBACK_LANGUAGE, initialInterfaceLanguage } from '@core/i18n/interface-language';

@Component({
    selector: 'app-root',
    imports: [
        RouterOutlet,
        ProgressSpinnerModule,
        DialogModule,
        ButtonModule,
        ToastModule,
        TranslateModule,
        NotificationComponent,
        DebugPanelComponent,
    ],
    template: `
        @if (loading()) {
            <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; height: 100vh;">
                <p-progressSpinner [style]="{ width: '45px', height: '45px' }" />
                <p style="font-size: 10px;">Powered by Mediaby <span style="font-size: 5px;">©</span></p>
            </div>
        } @else {
            <div class="app-container">
                <router-outlet></router-outlet>
            </div>
        }

        <p-dialog
            [visible]="sessionExpired.visible()"
            [modal]="true"
            [closable]="false"
            [style]="{ width: '420px' }"
            [header]="'login.sessionExpiredTitle' | translate"
        >
            <p>{{ 'login.sessionExpired' | translate }}</p>
            <ng-template #footer>
                <p-button [label]="'login.sessionExpiredButton' | translate" (onClick)="handleSessionExpiredLogout()" severity="secondary" rounded />
            </ng-template>
        </p-dialog>

        <p-toast position="top-right">
            <ng-template #message let-message>
                <app-notification [data]="message.data" />
            </ng-template>
        </p-toast>

        <p-toast key="reload" position="top-right">
            <ng-template #message let-message>
                <app-notification [data]="message.data" />
            </ng-template>
        </p-toast>

        <p-toast key="undo" position="bottom-center">
            <ng-template #message let-message>
                <div class="undo-toast-content">
                    <span>{{ 'creaManager.file.fileRemoved' | translate }}</span>
                    <button class="undo-toast-btn" (click)="message.data.onUndo()">{{ 'creaManager.file.cancel' | translate }}</button>
                </div>
            </ng-template>
        </p-toast>

        <app-debug-panel />
    `,
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
    private translate = inject(TranslateService);
    private grandCompteDns = inject(GrandCompteDnsService);
    private appFacade = inject(AppFacade);
    private notificationsService = inject(NotificationService);
    protected authService = inject(AuthService);
    protected sessionExpired = inject(SessionExpiredService);

    private router = inject(Router);
    private meta = inject(Meta);
    private currentUrl = toSignal(
        this.router.events.pipe(
            filter((e) => e instanceof NavigationEnd),
            map((e) => (e as NavigationEnd).urlAfterRedirects),
        ),
        { initialValue: this.router.url },
    );

    protected loading = signal(true);

    protected handleSessionExpiredLogout(): void {
        this.sessionExpired.visible.set(false);
        this.authService.logout();
    }

    constructor() {
        // MBY-459 : le français reste la langue de REPLI (clés manquantes), mais la langue active
        // suit le choix de l'utilisateur, puis le navigateur. La résolution définitive, qui tient
        // compte du grand compte du domaine, a lieu dans `GrandCompteDnsService`.
        this.translate.setDefaultLang(FALLBACK_LANGUAGE);
        this.translate.use(initialInterfaceLanguage());

        this.meta.updateTag({ name: 'app-version', content: environment.appVersion });
        this.meta.updateTag({ name: 'sentry-release', content: environment.sentryRelease });

        if (environment.production && typeof window !== 'undefined') {
            window.console.log = window.console.warn = window.console.info = () => {};
        }
    }

    async ngOnInit() {
        try {
            await this.notificationsService.generalConnect();

            await this.grandCompteDns.initializeGrandCompteByDns();

            if (this.appFacade.isAuthenticated && this.appFacade.currentUser) {
                const storedGcId = this.appFacade.currentGrandCompteId;

                if (storedGcId) {
                    await this.appFacade.restoreUserSession();
                }
            }

            this.loading.set(false);
        } catch (error) {
            console.error('Erreur initialisation app:', error);
            this.loading.set(false);
        }
    }
}
